import { DocumentData, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { firestoreWrapper } from './firestoreWrapper';
import { AVAILABLE_APPS } from '@/services/appAccess';

export interface UsageMetrics {
  activeUsers: {
    total: number;
    byApp: Record<string, number>;
  };
  goodThings: {
    total: number;
    fromApp: number;
    fromSheets: number;
  };
}

class UserActivityService {
  // Cache for aggregated metrics to avoid redundant queries
  private metricsCache: Map<string, { data: UsageMetrics, timestamp: number }> = new Map();
  private readonly METRICS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  
  // Debounce tracking to prevent excessive login tracking
  private loginTrackingDebounce: Map<string, number> = new Map();
  private readonly LOGIN_DEBOUNCE_TTL = 24 * 60 * 60 * 1000; // 24 hours - ensures unique daily visitors

  /**
   * Record a user's daily app access
   * @param userId The user's ID
   * @param userEmail The user's email
   * @param appId The app ID the user is accessing
   */
  async recordDailyAppAccess(userId: string, userEmail: string, appId: string): Promise<void> {
    try {
      // This is the method used in AuthContext.tsx
      return this.trackLogin(userId, appId);
    } catch (error) {
      
    }
  }

  /**
   * Track a user's login to the system
   * @param userId The user's ID
   * @param appId The app ID the user is accessing (optional)
   */
  async trackLogin(userId: string, appId?: string): Promise<void> {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      
      // Check if user has already been counted today - DATABASE IS SOURCE OF TRUTH
      const userActivityRef = firestoreWrapper.doc(
        firestoreWrapper.collection('userActivity'),
        userId
      );
      const userActivitySnap = await firestoreWrapper.getDoc(userActivityRef);
      const userData = userActivitySnap.exists() ? userActivitySnap.data() : {};
      
      // Apply debounce ONLY for performance, but database check is authoritative
      const debounceKey = `${userId}:${today}:${appId || 'hub'}`;
      const now = Date.now();
      const lastTracked = this.loginTrackingDebounce.get(debounceKey) || 0;
      
      // Check database first - if already counted today, update debounce and exit
      const todayKey = `counted_${today}`;
      const appKey = appId ? `counted_${today}_${appId}` : null;
      
      if (userData && userData[todayKey]) {
        // User already counted today - update debounce to prevent unnecessary writes
        this.loginTrackingDebounce.set(debounceKey, now);
        
        // Only update app-specific count if this is a new app for today
        if (appId && appKey && !userData[appKey]) {
          // This is a new app for today - proceed with app-specific tracking
          // Reset to continue below
        } else {
          // User already counted for this app today, skip
          return;
        }
      } else if (now - lastTracked < this.LOGIN_DEBOUNCE_TTL) {
        // Debounce protection: prevent multiple writes within 24 hours
        return;
      }
      
      // Update debounce timestamp for this user+date+app combo
      this.loginTrackingDebounce.set(debounceKey, now);
      
      // Track last login regardless of whether they've been counted today
      await firestoreWrapper.setDoc(userActivityRef, {
        lastLogin: Timestamp.now(),
        lastApp: appId || 'unknown'
      }, { merge: true });
      
      // Get metrics document reference for today
      const metricsRef = firestoreWrapper.doc(
        firestoreWrapper.collection('usageMetrics'), 
        today
      );
      
      // If user has already been counted today for any app
      if (userData && userData[todayKey]) {
        // Only update app-specific count if this is a new app for today
        if (appId && appKey && !userData[appKey]) {
          // Update user activity to mark this app as counted
          await firestoreWrapper.setDoc(userActivityRef, {
            [appKey]: true
          }, { merge: true });
          
          // Use a transaction to update the app-specific count
          await firestoreWrapper.runTransaction(db, async (transaction) => {
            const metricsDoc = await transaction.get(metricsRef);
            
            if (metricsDoc.exists()) {
              // Get current app count or default to 0
              const metricsData = metricsDoc.data();
              const currentAppCount = metricsData.activeUsers?.byApp?.[appId] || 0;
              
              // Update the app count
              transaction.update(metricsRef, {
                [`activeUsers.byApp.${appId}`]: currentAppCount + 1
              });
            } else {
              // Create new metrics document with app count
              const newMetrics: DocumentData = {
                date: today,
                activeUsers: {
                  total: 0, // Don't increment total since user already counted
                  byApp: {}
                },
                goodThings: {
                  total: 0,
                  fromApp: 0,
                  fromSheets: 0
                }
              };
              newMetrics.activeUsers.byApp[appId] = 1;
              transaction.set(metricsRef, newMetrics);
            }
          });
        }
        
        // User already counted today, no need to update total
        return;
      }
      
      // User hasn't been counted today, update their record
      const updateData: Record<string, any> = {
        [todayKey]: true,
        lastLogin: Timestamp.now(),
        lastApp: appId || 'unknown'
      };
      
      // Also mark this app as counted if provided
      if (appId && appKey) {
        updateData[appKey] = true;
      }
      
      // Use setDoc with merge option instead of updateDoc to handle the case
      // where the document doesn't exist yet
      await firestoreWrapper.setDoc(userActivityRef, updateData, { merge: true });
      
      // Use a transaction to update the metrics
      await firestoreWrapper.runTransaction(db, async (transaction) => {
        const metricsDoc = await transaction.get(metricsRef);
        
        if (metricsDoc.exists()) {
          // Update existing metrics document
          const metricsData = metricsDoc.data();
          
          // Get current counts or default to 0
          const currentTotal = metricsData.activeUsers?.total || 0;
          const currentAppCount = appId ? (metricsData.activeUsers?.byApp?.[appId] || 0) : 0;
          
          // Update total active users
          transaction.update(metricsRef, {
            'activeUsers.total': currentTotal + 1
          });
          
          // Update app-specific active users if appId is provided
          if (appId) {
            transaction.update(metricsRef, {
              [`activeUsers.byApp.${appId}`]: currentAppCount + 1
            });
          }
        } else {
          // Create new metrics document for today
          const newMetrics: DocumentData = {
            date: today,
            activeUsers: {
              total: 1,
              byApp: {}
            },
            goodThings: {
              total: 0,
              fromApp: 0,
              fromSheets: 0
            }
          };
          
          if (appId) {
            newMetrics.activeUsers.byApp[appId] = 1;
          }
          
          transaction.set(metricsRef, newMetrics);
        }
      });
      
    } catch (error) {
      
    }
  }
  
  /**
   * Track a Good Thing submission
   * @param source The source of the submission ('app' or 'sheets')
   */
  async trackGoodThingSubmission(source: 'app' | 'sheets'): Promise<void> {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const metricsRef = firestoreWrapper.doc(
        firestoreWrapper.collection('usageMetrics'),
        today
      );
      
      // Use a transaction to update the metrics
      await firestoreWrapper.runTransaction(db, async (transaction) => {
        const metricsDoc = await transaction.get(metricsRef);
        
        if (metricsDoc.exists()) {
          // Update existing metrics document
          const metricsData = metricsDoc.data();
          
          // Get current counts or default to 0
          const currentTotal = metricsData.goodThings?.total || 0;
          const currentFromApp = metricsData.goodThings?.fromApp || 0;
          const currentFromSheets = metricsData.goodThings?.fromSheets || 0;
          
          // Update total count
          transaction.update(metricsRef, {
            'goodThings.total': currentTotal + 1
          });
          
          // Update source-specific count
          if (source === 'app') {
            transaction.update(metricsRef, {
              'goodThings.fromApp': currentFromApp + 1
            });
          } else {
            transaction.update(metricsRef, {
              'goodThings.fromSheets': currentFromSheets + 1
            });
          }
        } else {
          // Create new metrics document for today
          const newMetrics: DocumentData = {
            date: today,
            activeUsers: {
              total: 0,
              byApp: {}
            },
            goodThings: {
              total: 1,
              fromApp: source === 'app' ? 1 : 0,
              fromSheets: source === 'sheets' ? 1 : 0
            }
          };
          
          transaction.set(metricsRef, newMetrics);
        }
      });
    } catch (error) {
      
    }
  }
  
  /**
   * Get aggregated metrics for a date range
   * @param startDate Start date in YYYY-MM-DD format
   * @param endDate End date in YYYY-MM-DD format
   */
  async getAggregatedMetrics(startDate: string, endDate: string): Promise<UsageMetrics> {
    try {
      // Check cache first
      const cacheKey = `${startDate}_${endDate}`;
      const cached = this.metricsCache.get(cacheKey);
      const now = Date.now();
      
      if (cached && now - cached.timestamp < this.METRICS_CACHE_TTL) {
        return cached.data;
      }
      
      // Use the firestoreWrapper to apply rate limiting
      const { firestoreWrapper } = await import('./firestoreWrapper');
      
      const metricsQuery = firestoreWrapper.query(
        firestoreWrapper.collection('usageMetrics'),
        firestoreWrapper.where('date', '>=', startDate),
        firestoreWrapper.where('date', '<=', endDate)
      );
      
      const metricsSnap = await firestoreWrapper.getDocs(metricsQuery);
      
      const aggregatedMetrics: UsageMetrics = {
        activeUsers: {
          total: 0,
          byApp: {}
        },
        goodThings: {
          total: 0,
          fromApp: 0,
          fromSheets: 0
        }
      };
      
      // Initialize byApp with all available apps
      Object.values(AVAILABLE_APPS).forEach(appId => {
        aggregatedMetrics.activeUsers.byApp[appId] = 0;
      });
      // Add hub app
      aggregatedMetrics.activeUsers.byApp['hub'] = 0;
      
      metricsSnap.forEach((document) => {
        const data = document.data();
        
        // Aggregate active users
        if (data.activeUsers) {
          aggregatedMetrics.activeUsers.total += data.activeUsers.total || 0;
          
          // Aggregate by app
          if (data.activeUsers.byApp) {
            Object.entries(data.activeUsers.byApp).forEach(([appId, count]) => {
              if (!aggregatedMetrics.activeUsers.byApp[appId]) {
                aggregatedMetrics.activeUsers.byApp[appId] = 0;
              }
              aggregatedMetrics.activeUsers.byApp[appId] += count as number;
            });
          }
        }
        
        // Aggregate Good Things
        if (data.goodThings) {
          aggregatedMetrics.goodThings.total += data.goodThings.total || 0;
          aggregatedMetrics.goodThings.fromApp += data.goodThings.fromApp || 0;
          aggregatedMetrics.goodThings.fromSheets += data.goodThings.fromSheets || 0;
        }
      });
      
      // Cache the result
      this.metricsCache.set(cacheKey, {
        data: aggregatedMetrics,
        timestamp: now
      });
      
      return aggregatedMetrics;
    } catch (error) {
      
      return {
        activeUsers: {
          total: 0,
          byApp: {}
        },
        goodThings: {
          total: 0,
          fromApp: 0,
          fromSheets: 0
        }
      };
    }
  }

  /**
   * Recalculate metrics for a specific date based on user activity data
   * This is useful for fixing incorrect metrics
   * @param date The date to recalculate metrics for in YYYY-MM-DD format
   */
  async recalculateMetricsForDate(date: string): Promise<void> {
    try {
      // Use the firestoreWrapper to apply rate limiting
      const { firestoreWrapper } = await import('./firestoreWrapper');
      
      // Get all user activity documents
      const userActivityQuery = firestoreWrapper.query(
        firestoreWrapper.collection('userActivity')
      );
      const userActivitySnap = await firestoreWrapper.getDocs(userActivityQuery);
      
      // Initialize metrics
      const metrics: DocumentData = {
        date,
        activeUsers: {
          total: 0,
          byApp: {}
        },
        goodThings: {
          // We can't recalculate Good Things submissions from user activity
          // so we'll preserve the existing values
          total: 0,
          fromApp: 0,
          fromSheets: 0
        }
      };
      
      // Initialize app counts
      Object.values(AVAILABLE_APPS).forEach(appId => {
        metrics.activeUsers.byApp[appId] = 0;
      });
      metrics.activeUsers.byApp['hub'] = 0;
      
      // Count users and app access
      const todayKey = `counted_${date}`;
      const appKeyPrefix = `counted_${date}_`;
      
      userActivitySnap.forEach((document) => {
        const userData = document.data();
        
        // Check if user was counted on this date
        if (userData[todayKey]) {
          // Increment total user count
          metrics.activeUsers.total += 1;
          
          // Check which apps the user accessed
          Object.keys(userData).forEach(key => {
            if (key.startsWith(appKeyPrefix) && userData[key] === true) {
              const appId = key.substring(appKeyPrefix.length);
              if (!metrics.activeUsers.byApp[appId]) {
                metrics.activeUsers.byApp[appId] = 0;
              }
              metrics.activeUsers.byApp[appId] += 1;
            }
          });
        }
      });
      
      // Get existing metrics reference
      const metricsRef = firestoreWrapper.doc(
        firestoreWrapper.collection('usageMetrics'),
        date
      );
      
      // NOTE: We intentionally do NOT preserve old Good Things data here because:
      // 1. Recalculate is meant to fix corrupted data
      // 2. Good Things tracking happens in real-time via trackGoodThingSubmission()
      // 3. Old accumulated data (like the 1789 issue) should be reset to 0
      // Going forward, new submissions will be tracked correctly via trackGoodThingSubmission()
      
      // Update metrics document - this will reset goodThings tracking to 0 for this date
      await firestoreWrapper.setDoc(metricsRef, metrics);
      
      console.log(`✅ Recalculated metrics for ${date}:`, {
        activeUsers: metrics.activeUsers.total,
        goodThingsReset: 'fromApp and fromSheets reset to 0 (will be tracked going forward)'
      });
      
      // Clear cache for this date
      this.clearMetricsCache();
    } catch (error) {
      
    }
  }
  
  /**
   * Clear the metrics cache
   */
  clearMetricsCache(): void {
    this.metricsCache.clear();
  }
}

export const userActivityService = new UserActivityService();
